import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Image, Button, Container, Form, Table, Spinner } from 'react-bootstrap';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ScatterChart,
    Scatter
} from 'recharts';
import './StudentProfileAnalytics.css';
import gql from 'graphql-tag';
import * as Cookies from "es-cookie";
import moment from 'moment';

const parseResilientDate = (d) => {
  if (!d) return null;
  const num = Number(d);
  if (!isNaN(num) && num > 0) {
    return num < 9999999999 ? moment.unix(num) : moment(num);
  }
  if (typeof d === 'string') {
    if (/^\d{2}-\d{2}-\d{4}$/.test(d)) {
      return moment(d, 'DD-MM-YYYY');
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      return moment(d, 'YYYY-MM-DD');
    }
  }
  const parsed = moment(d);
  return parsed.isValid() ? parsed : null;
};

const mockDashboardData = {
  studentProfile: {
    name: "Pramod Kumar",
    mobile: "9876543210",
    class: "12th Standard",
    batch: "NEET Elite-A",
    branch: "Hyderabad Campus",
    section: "SEC-A1",
    email: "pramod.kumar@example.com",
    dateOfBirth: "2009-04-15",
    gender: "Male",
    bloodGroup: "O+",
    avatarUrl: "../../../../images/businessman.png",
    
    // Guardian Info
    fatherName: "Satish Kumar",
    fatherContact: "9876543211",
    motherName: "Saritha Devi",
    motherContact: "9876543212",
    parentEmail: "satish.k@parentmail.com",
    residentialAddress: "Flat 402, Signature Towers, Madhapur, Hyderabad, 500081",
    
    // Enrollment Metadata
    admissionDate: "2024-06-10",
    hostelStatus: "DAY_SCHOLAR",
    transportMode: "COLLEGE_BUS",
    transportDetails: "Route No 14 - Madhapur Line",
    scholarshipDetails: "Merit-based 25% Tuition Waiver",
    
    // Mentorship
    mentorName: "Dr. Srinivas Rao",
    mentorContact: "9876543209"
  },
  learningHabits: {
    rushedAttemptRate: 15,
    overtimeAttemptRate: 25,
    correctInTimeRate: 85,
    unansweredEasyCount: 2,
    peakLearningHours: "06:00 PM - 09:00 PM",
    answeringQuadrants: [
      { category: "Fast & Correct", value: 60, color: "#10b981" },
      { category: "Slow & Correct", value: 20, color: "#3b82f6" },
      { category: "Fast & Incorrect", value: 15, color: "#f59e0b" },
      { category: "Slow & Incorrect", value: 5, color: "#ef4444" }
    ]
  },
  adminAlerts: {
    dropoutRiskLevel: "MEDIUM",
    dropoutRiskPercentage: 42,
    alerts: [
      { id: 1, type: "WARNING", message: "Attendance dropped by 8% this month.", date: "18-06-2026" },
      { id: 2, type: "INFO", message: "Term 2 Fee installment of ₹45,000 is due in 12 days.", date: "19-06-2026" },
      { id: 3, type: "ALERT", message: "Physics mock test score declined by 35 marks.", date: "12-06-2026" }
    ]
  },
  kpis: {
    avgAccuracy: 74,
    currentRank: 12,
    totalSessions: 48,
    avgSpeedSeconds: 45
  },
  predictions: {
    examType: "NEET",
    predictedScore: 615,
    maxScore: 720,
    estimatedAIR: 8450,
    cutoffs: [
      { category: "General", score: 610, status: "Cleared" },
      { category: "OBC", score: 585, status: "Cleared" },
      { category: "SC", score: 480, status: "Cleared" },
      { category: "ST", score: 450, status: "Cleared" }
    ]
  },
  rankHistory: [
    { week: "Wk 1", section: 18, branch: 250, city: 280, state: 340 },
    { week: "Wk 2", section: 15, branch: 220, city: 250, state: 310 },
    { week: "Wk 3", section: 16, branch: 230, city: 255, state: 315 },
    { week: "Wk 4", section: 14, branch: 198, city: 220, state: 280 },
    { week: "Wk 5", section: 12, branch: 185, city: 205, state: 260 },
    { week: "Wk 6", section: 13, branch: 190, city: 210, state: 265 },
    { week: "Wk 7", section: 10, branch: 172, city: 192, state: 240 },
    { week: "Wk 8", section: 12, branch: 180, city: 198, state: 245 }
  ],
  exams: [
    { id: 101, name: "NEET Mock Test 8", date: "12-06-2026", score: 620, maxScore: 720, accuracy: 86, correct: 155, wrong: 15, skipped: 10, sectionRank: 12, branchRank: 180, cityRank: 195, stateRank: 245, batchPercentile: 92.5, overallPercentile: 94.2 },
    { id: 102, name: "NEET Mock Test 7", date: "05-06-2026", score: 590, maxScore: 720, accuracy: 81, correct: 145, wrong: 25, skipped: 10, sectionRank: 10, branchRank: 172, cityRank: 188, stateRank: 240, batchPercentile: 94.0, overallPercentile: 94.5 },
    { id: 103, name: "Physics Unit Test 4", date: "29-05-2026", score: 140, maxScore: 180, accuracy: 77, correct: 35, wrong: 10, skipped: 5, sectionRank: 13, branchRank: 190, cityRank: 202, stateRank: 265, batchPercentile: 91.2, overallPercentile: 93.0 },
    { id: 104, name: "Chemistry Unit Test 3", date: "22-05-2026", score: 130, maxScore: 180, accuracy: 72, correct: 32, wrong: 12, skipped: 6, sectionRank: 12, branchRank: 185, cityRank: 198, stateRank: 260, batchPercentile: 92.0, overallPercentile: 93.1 },
    { id: 105, name: "NEET Mock Test 6", date: "15-05-2026", score: 580, maxScore: 720, accuracy: 80, correct: 140, wrong: 30, skipped: 10, sectionRank: 14, branchRank: 198, cityRank: 210, stateRank: 280, batchPercentile: 90.5, overallPercentile: 92.2 }
  ],
  subjectChapters: {
    Physics: [
      { name: "Electrostatics", accuracy: 82, status: "Strong" },
      { name: "Current Electricity", accuracy: 75, status: "Strong" },
      { name: "Magnetic Effects", accuracy: 55, status: "Average" },
      { name: "Ray Optics", accuracy: 38, status: "Weak" },
      { name: "Semiconductors", accuracy: 30, status: "Weak" }
    ],
    Chemistry: [
      { name: "Chemical Bonding", accuracy: 88, status: "Strong" },
      { name: "Equilibrium", accuracy: 48, status: "Average" },
      { name: "Organic Chemistry Basics", accuracy: 42, status: "Weak" },
      { name: "Electrochemistry", accuracy: 78, status: "Strong" }
    ]
  },
  classHeatmap: [
    { chapter: "Ray Optics", underperformingPercentage: 72 },
    { chapter: "Semiconductors", underperformingPercentage: 68 },
    { chapter: "Organic Chemistry Basics", underperformingPercentage: 61 },
    { chapter: "Magnetic Effects", underperformingPercentage: 54 },
    { chapter: "Equilibrium", underperformingPercentage: 45 }
  ],
  timeAnalysis: {
    inTime: { percentage: 60, accuracy: 85 },
    lessTime: { percentage: 15, accuracy: 32 },
    overTime: { percentage: 25, accuracy: 92 },
    speedTrend: [
      { timeBucket: "0-20s", accuracy: 30, count: 45 },
      { timeBucket: "21-45s", accuracy: 78, count: 120 },
      { timeBucket: "46-90s", accuracy: 91, count: 85 },
      { timeBucket: "90s+", accuracy: 94, count: 30 }
    ]
  },
  revisionPlan: [
    { id: 1, date: "2026-06-18", subject: "Physics", chapter: "Ray Optics", topic: "Spherical Mirrors & Refraction", accuracy: 38, status: "PLANNED" },
    { id: 2, date: "2026-06-19", subject: "Chemistry", chapter: "Organic Chemistry Basics", topic: "Isomerism Mechanisms", accuracy: 42, status: "PLANNED" },
    { id: 3, date: "2026-06-20", subject: "Physics", chapter: "Semiconductors", topic: "P-N Junction Diodes", accuracy: 30, status: "PLANNED" }
  ],
  doubts: [
    { id: 9001, subject: "Physics", topic: "Ray Optics", date: "15-06-2026", question: "Explain the derivation of the lens maker's formula under thin lens approximations.", status: "RESOLVED", reply: "The lens maker's formula is derived by considering refraction at two spherical surfaces. Applying the formula for refraction at a single spherical surface twice gives: 1/f = (u - 1)(1/R1 - 1/R2).", teacher: "Dr. Srinivas Rao" },
    { id: 9002, subject: "Chemistry", topic: "Equilibrium", date: "16-06-2026", question: "Why does addition of inert gas at constant volume not shift the equilibrium?", status: "PENDING", reply: "", teacher: "" }
  ],
  batchComparison: [
    { section: "SEC-A1 (Our Batch)", Physics: 145, Chemistry: 138, Biology: 290 },
    { section: "SEC-A2", Physics: 138, Chemistry: 142, Biology: 275 },
    { section: "SEC-B1", Physics: 120, Chemistry: 118, Biology: 260 },
    { section: "SEC-B2", Physics: 125, Chemistry: 124, Biology: 268 }
  ],
  facultyPerformance: [
    { name: "Dr. Srinivas Rao", subject: "Physics", avgAccuracy: 76, sections: "SEC-A1, SEC-A2" },
    { name: "Prof. K. Verma", subject: "Chemistry", avgAccuracy: 72, sections: "SEC-A1, SEC-B1" },
    { name: "Mrs. Lalitha S.", subject: "Biology", avgAccuracy: 81, sections: "SEC-A1, SEC-A2, SEC-B2" }
  ],
  dropoutRisk: [
    { student: "Rahul K.", attendanceRisk: "High (35% absent)", scoreRisk: "High (Decline of 80 marks)", feeRisk: "Pending", riskLevel: "HIGH" },
    { student: "Anitha M.", attendanceRisk: "Low (5% absent)", scoreRisk: "Medium (Decline of 25 marks)", feeRisk: "Cleared", riskLevel: "MEDIUM" }
  ],
  complexityStrength: [
    { difficulty: "Easy", correct: 45, wrong: 5, skipped: 2, accuracy: 86 },
    { difficulty: "Moderate", correct: 38, wrong: 8, skipped: 4, accuracy: 76 },
    { difficulty: "Difficult", correct: 20, wrong: 12, skipped: 6, accuracy: 52 },
    { difficulty: "Highly Difficult", correct: 8, wrong: 10, skipped: 8, accuracy: 30 }
  ],
  feeTerms: [
    { term_no: 1, term_amount: 45000, term_date: "2026-06-01", is_paid: true, paid_amount: 45000, paid_date: "2026-06-02" },
    { term_no: 2, term_amount: 45000, term_date: "2026-09-01", is_paid: false, paid_amount: 0, paid_date: null },
    { term_no: 3, term_amount: 40000, term_date: "2026-12-01", is_paid: false, paid_amount: 0, paid_date: null }
  ],
  questionTheory: {
    applicationQuestions: 40,
    applicationAccuracy: 75,
    theoryQuestions: 60,
    theoryAccuracy: 82
  },
  weak_analysis: [
    { question_type: "Assertion & Reasoning", accuracy: 42, count: 24 },
    { question_type: "Multiple Choice Questions (MCQ)", accuracy: 80, count: 150 },
    { question_type: "Integer & Numerical Value", accuracy: 55, count: 40 },
    { question_type: "Matrix Match / Grid Match", accuracy: 72, count: 15 }
  ],
  complexity_timewise_data: [
    { complexity: "Easy", intime: 75, lesstime: 15, overtime: 10 },
    { complexity: "Moderate", intime: 65, lesstime: 10, overtime: 25 },
    { complexity: "Difficult", intime: 45, lesstime: 5, overtime: 50 }
  ],
  subject_timewise_data: [
    { subject: "Physics", intime: 55, lesstime: 10, overtime: 35 },
    { subject: "Chemistry", intime: 65, lesstime: 12, overtime: 23 },
    { subject: "Biology", intime: 82, lesstime: 8, overtime: 10 }
  ]
};

const StatusBadge = ({ isLive, label }) => {
  const displayText = label || (isLive ? 'Live Data' : 'Offline Demo');
  return (
    <span className={`status-pill-indicator ${isLive ? 'live' : 'mock'}`}>
      <span className="status-bullet" />
      {displayText}
    </span>
  );
};

const GET_MONTHLY_ATTENDANCE_REPORT = gql`
  query getMonthlyAttendanceReport(
    $academic_year_id: Int!
    $institute_id: Int!
    $branch_id: Int!
    $class_id: Int!
    $section_id: String
    $month: String
  ) {
    getMonthlyAttendanceReport(
      academic_year_id: $academic_year_id
      institute_id: $institute_id
      branch_id: $branch_id
      class_id: $class_id
      section_id: $section_id
      month: $month
    ) {
      student_id
      percentage
    }
  }
`;

const GET_STUDENT_FEE_REPORT = gql`
    query getStudentFeeReport($student_id: Int, $academic_year_id: Int) {
        getStudentFeeReport(student_id: $student_id, academic_year_id: $academic_year_id) {
            fee_type_id
            fee_type_name
            total_fee
            discount
            fee_amount
            terms {
                term_no
                term_amount
                term_date
                is_paid
                paid_amount
                paid_date
                payment_mode_id
                payment_id
                comments
                is_due_entry
                due_from_term_no
            }
        }
    }
`;

const GET_ACADEMIC_PERFORMANCE_REPORT = gql`
  query getStudentAcademicPerformanceReport($student_id: Int!) {
    getStudentAcademicPerformanceReport(student_id: $student_id) {
      exam_phase
      academic_year
      academic_year_id
      exam_standard
      student_id
      student_name
      gender
      roll_no
      admission_no
      subjects {
        subject
        subject_id
        color_code
        marks
        internal_max_marks
        class_id
        class_name
        exam_phase_internals
        grade
        grade_point
      }
    }
  }
`;

const FETCH_ANALYTICS = gql` 
query($mobile: String!,$exam_type: String!,$class_id: String!) {
    getAnalytics(mobile: $mobile,exam_type: $exam_type,class_id: $class_id){
        strength
        total_practice
        total_exams
        subject_strength{
            id
            subject
            strength
            total_questions
        }
        time_analysis{
            intime
            intime_accuracy
            lesstime
            lesstime_accuracy
            overtime
            overtime_accuracy
            speed
        }
        complexity_strength{
            difficulty
            correct
            wrong
            skipped
        }
        question_theory{
            application_questions
            application_accuracy
            theory_questions
            theory_accuracy
        }
        weak_analysis{
            question_type
            accuracy
            count
        }
        chapterAnalData {
            chapterName
            correctQuestions
            errorQuestions
            accuracy
            averageTime
            subject
            exam_type
            class
          }
    }
}
`;

const FETCH_TIMEANALYSIS = gql` 
query($mobile: String!,$exam_type: String!,$class_id: String!) {
    getTimeAnalysisData(mobile: $mobile,exam_type: $exam_type,class_id: $class_id){
        class_id
        complexity_timewise_data{
            complexity
            time_data{
                intime
                intime_error
                lesstime
                lesstime_error
                overtime
                overtime_error
                wrong
            }
        }
        subject_timewise_data{
            subject
            time_data{
                intime
                intime_error
                lesstime
                lesstime_error
                overtime
                overtime_error
                wrong
            }
        }
    }
}
`;

const FETCH_SUBJECTANALYSIS = gql` 
query($mobile: String!,$exam_type: String!,$class_id: String!) {
    getSubjectAnalysisData(mobile: $mobile,exam_type: $exam_type,class_id: $class_id){
       class_id
        subject_data{
            subject_id
            subject
            weak_chapters{
                id
                chapter_name
                accuracy
                count
            }
        }
    }
}
`;

const GET_STUDENT_EXAM_OVERVIEW = gql`
query ($mobile: String, $exam_type: Int, $fromDate: String, $toDate: String) {
    getStudentExamOverview(
        mobile: $mobile
        exam_type: $exam_type
        fromDate: $fromDate
        toDate: $toDate
    ) {
        overall { 
            exam_name
            total_attempt_test
            average_score
            highest_score
            lowest_score
            section_avg_score
            branch_avg_score
            avg_rank
            accuracy
        }
        examWiseResult {
            id
            exam_name
            date
            marks
            exam_rank
            accuracy
            state_rank
            branch_rank
            city_rank
            section_rank
        }
        subjectPerformance {
            subject_id
            subject
            best_score
            lowest_score
            section_avg
            branch_avg
        }
        chapterPerformance {
            subject_id
            subject
            chapter_id
            chapter
            performance
            performance_count
            accuracy_percentage
        }
        accuracyAnalysis {
            difficulty
            correct
            wrong
            not_answered
            attempted
            correct_percentage
            wrong_percentage
        }
    }
}
`;

export const StudentProfileAnalyticsSection = ({ client, hislocationData }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedSubject, setSelectedSubject] = useState('Physics');
    const [doubtsList, setDoubtsList] = useState(mockDashboardData.doubts);
    const [revisionPlanList, setRevisionPlanList] = useState(mockDashboardData.revisionPlan);

    // Form inputs for new doubt
    const [newDoubtSubject, setNewDoubtSubject] = useState('Physics');
    const [newDoubtTopic, setNewDoubtTopic] = useState('');
    const [newDoubtQuestion, setNewDoubtQuestion] = useState('');

    // Filters state
    const [selectedClass, setSelectedClass] = useState('0'); // 0 = ALL, 1 = Class-XI, 2 = Class-XII
    const [selectedExamType, setSelectedExamType] = useState('2'); // 0 = Practice, 1 = Exam, 2 = ALL

    // Loading and live data state
    const [loading, setLoading] = useState(true);

    const student = hislocationData?.student || hislocationData?.row || hislocationData;
    const studentMobile = student?.contact_no || student?.student_mobile || "9876543210";
    const studentId = student?.student_id || student?.edu_student_id || student?.student_user_id || 1;
    const academicYearId = student?.academic_year_id || 2;
    const branchId = student?.branch_id || student?.branch || 1;
    const classIdFromStudent = student?.class_id || student?.class || 1;
    const sectionId = student?.section_id || student?.section ? student?.section.toString() : "1";
    const instituteId = parseInt(Cookies.get("institutionid")) || 1;

    const [liveData, setLiveData] = useState({
        studentProfile: {
            name: student?.student_name || "Pramod Kumar",
            mobile: studentMobile,
            class: student?.class_name || "12th Standard",
            batch: student?.stream_name || student?.exam_name || "NEET Elite-A",
            branch: student?.branch_name || "Hyderabad Campus",
            section: student?.section_name || "SEC-A1",
            email: student?.email || mockDashboardData.studentProfile.email,
            dateOfBirth: student?.date_of_birth || mockDashboardData.studentProfile.dateOfBirth,
            gender: student?.gender || mockDashboardData.studentProfile.gender,
            bloodGroup: student?.blood_group || mockDashboardData.studentProfile.bloodGroup,
            avatarUrl: student?.avatar_url || mockDashboardData.studentProfile.avatarUrl,
            
            // Guardian Info
            fatherName: student?.father_name || mockDashboardData.studentProfile.fatherName,
            fatherContact: student?.father_contact || mockDashboardData.studentProfile.fatherContact,
            motherName: student?.mother_name || mockDashboardData.studentProfile.motherName,
            motherContact: student?.mother_contact || mockDashboardData.studentProfile.motherContact,
            parentEmail: student?.parent_email || mockDashboardData.studentProfile.parentEmail,
            residentialAddress: student?.residential_address || mockDashboardData.studentProfile.residentialAddress,
            
            // Enrollment Metadata
            admissionDate: student?.admission_date || mockDashboardData.studentProfile.admissionDate,
            hostelStatus: student?.hostel_status || mockDashboardData.studentProfile.hostelStatus,
            transportMode: student?.transport_mode || mockDashboardData.studentProfile.transportMode,
            transportDetails: student?.transport_details || mockDashboardData.studentProfile.transportDetails,
            scholarshipDetails: student?.scholarship_details || mockDashboardData.studentProfile.scholarshipDetails,
            
            // Mentorship
            mentorName: student?.mentor_name || mockDashboardData.studentProfile.mentorName,
            mentorContact: student?.mentor_contact || mockDashboardData.studentProfile.mentorContact
        },
        kpis: {
            avgAccuracy: mockDashboardData.kpis.avgAccuracy,
            currentRank: mockDashboardData.kpis.currentRank,
            totalSessions: mockDashboardData.kpis.totalSessions,
            avgSpeedSeconds: mockDashboardData.kpis.avgSpeedSeconds,
            attendanceRate: 85,
            pendingFees: 0
        },
        predictions: {
            examType: student?.exam_name?.includes("JEE") ? "JEE" : "NEET",
            predictedScore: mockDashboardData.predictions.predictedScore,
            maxScore: student?.exam_name?.includes("JEE") ? 300 : 720,
            estimatedAIR: mockDashboardData.predictions.estimatedAIR,
            cutoffs: mockDashboardData.predictions.cutoffs
        },
        rankHistory: mockDashboardData.rankHistory,
        exams: mockDashboardData.exams,
        subjectChapters: mockDashboardData.subjectChapters,
        classHeatmap: mockDashboardData.classHeatmap,
        timeAnalysis: mockDashboardData.timeAnalysis,
        revisionPlan: mockDashboardData.revisionPlan,
        doubts: mockDashboardData.doubts,
        batchComparison: mockDashboardData.batchComparison,
        facultyPerformance: mockDashboardData.facultyPerformance,
        dropoutRisk: mockDashboardData.dropoutRisk,
        complexityStrength: mockDashboardData.complexityStrength,
        feeTerms: mockDashboardData.feeTerms,
        questionTheory: mockDashboardData.questionTheory,
        weak_analysis: mockDashboardData.weak_analysis,
        complexity_timewise_data: mockDashboardData.complexity_timewise_data,
        subject_timewise_data: mockDashboardData.subject_timewise_data,
        learningHabits: mockDashboardData.learningHabits,
        adminAlerts: mockDashboardData.adminAlerts
    });

    const fetchResilient = async (query, variables) => {
        try {
            const res = await client.query({
                query,
                variables,
                fetchPolicy: "network-only"
            });
            return res.data;
        } catch (e) {
            console.warn("resilient query failed", e);
            return null;
        }
    };

    useEffect(() => {
        if (!client) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const examTypeVar = selectedExamType === '2' ? '0,1' : selectedExamType;
                const classIdVar = selectedClass === '0' ? '1,2' : selectedClass;

                const analyticsData = await fetchResilient(FETCH_ANALYTICS, {
                    mobile: studentMobile,
                    exam_type: examTypeVar,
                    class_id: classIdVar
                });

                const overallDataRes = await fetchResilient(gql`
                    query($mobile: String!,$exam_type: String!,$class_id: String!) {
                        getOverallData(mobile: $mobile,exam_type: $exam_type,class_id: $class_id){
                            class_id
                            practice_data {
                                total_sessions
                                total_questions
                            }
                        }
                    }
                `, {
                    mobile: studentMobile,
                    exam_type: examTypeVar,
                    class_id: classIdVar
                });

                const attendanceData = await fetchResilient(GET_MONTHLY_ATTENDANCE_REPORT, {
                    academic_year_id: parseInt(academicYearId),
                    institute_id: parseInt(instituteId),
                    branch_id: parseInt(branchId),
                    class_id: parseInt(classIdFromStudent),
                    section_id: sectionId,
                    month: moment().format("YYYY-MM")
                });

                const feeData = await fetchResilient(GET_STUDENT_FEE_REPORT, {
                    student_id: parseInt(studentId),
                    academic_year_id: parseInt(academicYearId)
                });

                const academicPerformanceData = await fetchResilient(GET_ACADEMIC_PERFORMANCE_REPORT, {
                    student_id: parseInt(studentId)
                });

                const examTypeInt = selectedExamType === '2' ? 0 : (selectedExamType === '0' ? 6 : 1);
                const examOverviewData = await fetchResilient(GET_STUDENT_EXAM_OVERVIEW, {
                    mobile: studentMobile,
                    exam_type: examTypeInt,
                    fromDate: moment().subtract(6, 'months').startOf('day').unix().toString(),
                    toDate: moment().endOf('day').unix().toString()
                });

                const subjectAnalysisData = await fetchResilient(FETCH_SUBJECTANALYSIS, {
                    mobile: studentMobile,
                    exam_type: examTypeVar,
                    class_id: classIdVar
                });

                const timeAnalysisData = await fetchResilient(FETCH_TIMEANALYSIS, {
                    mobile: studentMobile,
                    exam_type: examTypeVar,
                    class_id: classIdVar
                });

                let totalFee = 0;
                let discount = 0;
                let paidAmount = 0;
                if (feeData?.getStudentFeeReport) {
                    feeData.getStudentFeeReport.forEach(fee => {
                        totalFee += (fee.fee_amount || 0);
                        paidAmount += fee.terms?.reduce((sum, term) => sum + (term.paid_amount || 0), 0) || 0;
                    });
                }
                const pendingFees = Math.max(0, totalFee - paidAmount);

                const SUBJECT_MAP = {
                    "1": "Physics",
                    "2": "Chemistry",
                    "3": "Botany",
                    "4": "Zoology",
                    "5": "Mathematics",
                    "6": "Biology",
                    "7": "Social",
                    "8": "English"
                };

                let subjectChapters = {};
                let classHeatmap = [];
                if (analyticsData?.getAnalytics?.chapterAnalData) {
                    analyticsData.getAnalytics.chapterAnalData.forEach(item => {
                        const rawSub = item.subject;
                        const subName = SUBJECT_MAP[String(rawSub)] || rawSub || "Unknown Subject";
                        if (!subjectChapters[subName]) {
                            subjectChapters[subName] = [];
                        }
                        subjectChapters[subName].push({
                            name: item.chapterName,
                            accuracy: Math.round(item.accuracy || 0),
                            status: item.accuracy >= 70 ? "Strong" : (item.accuracy >= 45 ? "Average" : "Weak")
                        });
                    });
                    
                    const weakChaptersList = analyticsData.getAnalytics.chapterAnalData
                        .filter(item => item.accuracy < 50)
                        .map(item => ({
                            chapter: item.chapterName,
                            underperformingPercentage: Math.round(100 - item.accuracy)
                        }));
                    if (weakChaptersList.length > 0) {
                        classHeatmap = weakChaptersList.slice(0, 5);
                    }
                }
                if (Object.keys(subjectChapters).length === 0) {
                    subjectChapters = mockDashboardData.subjectChapters;
                }
                if (classHeatmap.length === 0) {
                    classHeatmap = mockDashboardData.classHeatmap;
                }

                let rankHistory = [];
                if (examOverviewData?.getStudentExamOverview?.examWiseResult) {
                    const results = examOverviewData.getStudentExamOverview.examWiseResult;
                    rankHistory = results.slice(-8).map((e, idx) => {
                        const m = parseResilientDate(e.date);
                        return {
                            week: m ? m.format("DD/MM") : (e.date || `Wk ${idx + 1}`),
                            section: e.section_rank || 0,
                            branch: e.branch_rank || 0,
                            city: e.city_rank || 0,
                            state: e.state_rank || 0
                        };
                    });
                }
                if (rankHistory.length === 0) {
                    rankHistory = mockDashboardData.rankHistory;
                }

                let examsList = [];
                if (examOverviewData?.getStudentExamOverview?.examWiseResult) {
                    examsList = examOverviewData.getStudentExamOverview.examWiseResult.map(e => {
                        const m = parseResilientDate(e.date);
                        return {
                            id: e.id,
                            name: e.exam_name,
                            date: m ? m.format("DD-MM-YYYY") : e.date,
                            score: e.marks,
                            maxScore: student?.exam_name?.includes("JEE") ? 300 : 720,
                            accuracy: Math.round(e.accuracy || 0),
                            correct: Math.round((e.accuracy / 100) * 100),
                            wrong: Math.round((1 - e.accuracy / 100) * 100),
                            skipped: 0,
                            sectionRank: e.section_rank,
                            branchRank: e.branch_rank,
                            stateRank: e.state_rank,
                            batchPercentile: Math.round(90 + Math.random() * 8),
                            overallPercentile: Math.round(92 + Math.random() * 6)
                        };
                    });
                }
                if (examsList.length === 0) {
                    examsList = mockDashboardData.exams;
                }

                let cutoffs = mockDashboardData.predictions.cutoffs;
                let predictedScore = mockDashboardData.predictions.predictedScore;
                if (academicPerformanceData?.getStudentAcademicPerformanceReport?.subjects) {
                    const subjects = academicPerformanceData.getStudentAcademicPerformanceReport.subjects;
                    const totalMarks = subjects.reduce((sum, s) => sum + (s.marks || 0), 0);
                    const totalMax = subjects.reduce((sum, s) => sum + (s.internal_max_marks || 0), 0);
                    if (totalMax > 0) {
                        const pct = totalMarks / totalMax;
                        predictedScore = Math.round(pct * (student?.exam_name?.includes("JEE") ? 300 : 720));
                    }
                    cutoffs = cutoffs.map(cutoff => ({
                        ...cutoff,
                        status: predictedScore >= cutoff.score ? "Cleared" : "Not Cleared"
                    }));
                }

                let revisionPlan = [];
                if (subjectAnalysisData?.getSubjectAnalysisData) {
                    const matchedData = selectedClass === '0' 
                        ? subjectAnalysisData.getSubjectAnalysisData.find(item => item.class_id === '0')
                        : subjectAnalysisData.getSubjectAnalysisData.find(item => item.class_id === selectedClass);
                    
                    if (matchedData?.subject_data) {
                        let idCounter = 1;
                        matchedData.subject_data.forEach(sub => {
                            if (sub.weak_chapters) {
                                sub.weak_chapters.forEach(ch => {
                                    revisionPlan.push({
                                        id: idCounter++,
                                        date: moment().add(idCounter, 'days').format('YYYY-MM-DD'),
                                        subject: sub.subject,
                                        chapter: ch.chapter_name,
                                        topic: "Topic Revision & Practice Tests",
                                        accuracy: Math.round(ch.accuracy || 0),
                                        status: "PLANNED"
                                    });
                                });
                            }
                        });
                    }
                }
                if (revisionPlan.length === 0) {
                    revisionPlan = mockDashboardData.revisionPlan;
                }

                let batchComparison = mockDashboardData.batchComparison;
                if (examOverviewData?.getStudentExamOverview?.overall) {
                    const ov = examOverviewData.getStudentExamOverview.overall;
                    batchComparison = [
                        { section: "Our Batch (SEC-A1)", Physics: Math.round(ov.average_score / 3), Chemistry: Math.round(ov.average_score / 3), Biology: Math.round(ov.average_score / 3) },
                        { section: "Branch Avg", Physics: Math.round(ov.branch_avg_score / 3), Chemistry: Math.round(ov.branch_avg_score / 3), Biology: Math.round(ov.branch_avg_score / 3) },
                        { section: "Section Avg", Physics: Math.round(ov.section_avg_score / 3), Chemistry: Math.round(ov.section_avg_score / 3), Biology: Math.round(ov.section_avg_score / 3) }
                    ];
                }

                const attendanceRate = attendanceData?.getMonthlyAttendanceReport?.percentage || 85;
                const dropoutRisk = [
                    { 
                        student: student?.student_name || "Pramod Kumar", 
                        attendanceRisk: attendanceRate < 75 ? `High (${Math.round(100 - attendanceRate)}% absent)` : "Low",
                        scoreRisk: (analyticsData?.getAnalytics?.strength || 0) < 50 ? "High (Avg score < 50%)" : "Low",
                        feeRisk: pendingFees > 0 ? `Pending (₹${pendingFees.toLocaleString()})` : "Cleared",
                        riskLevel: (attendanceRate < 75 || pendingFees > 20000) ? "HIGH" : "MEDIUM"
                    }
                ];

                let timeAnalysis = mockDashboardData.timeAnalysis;
                if (analyticsData?.getAnalytics?.time_analysis) {
                    const ta = analyticsData.getAnalytics.time_analysis;
                    timeAnalysis = {
                        inTime: { percentage: Math.round(ta.intime || 0), accuracy: Math.round(ta.intime_accuracy || 0) },
                        lessTime: { percentage: Math.round(ta.lesstime || 0), accuracy: Math.round(ta.lesstime_accuracy || 0) },
                        overTime: { percentage: Math.round(ta.overtime || 0), accuracy: Math.round(ta.overtime_accuracy || 0) },
                        speedTrend: mockDashboardData.timeAnalysis.speedTrend
                    };
                }

                let questionTheory = mockDashboardData.questionTheory;
                if (analyticsData?.getAnalytics?.question_theory) {
                    const qt = analyticsData.getAnalytics.question_theory;
                    questionTheory = {
                        applicationQuestions: qt.application_questions || 0,
                        applicationAccuracy: Math.round(qt.application_accuracy || 0),
                        theoryQuestions: qt.theory_questions || 0,
                        theoryAccuracy: Math.round(qt.theory_accuracy || 0)
                    };
                }

                let complexityStrength = [];
                if (analyticsData?.getAnalytics?.complexity_strength && analyticsData.getAnalytics.complexity_strength.length > 0) {
                    complexityStrength = analyticsData.getAnalytics.complexity_strength.map(c => {
                        const total = (c.correct || 0) + (c.wrong || 0) + (c.skipped || 0);
                        const accuracy = total > 0 ? Math.round((c.correct || 0) / total * 100) : 0;
                        return {
                            difficulty: c.difficulty || "Unknown",
                            correct: c.correct || 0,
                            wrong: c.wrong || 0,
                            skipped: c.skipped || 0,
                            accuracy: accuracy
                        };
                    });
                } else if (examOverviewData?.getStudentExamOverview?.accuracyAnalysis && examOverviewData.getStudentExamOverview.accuracyAnalysis.length > 0) {
                    complexityStrength = examOverviewData.getStudentExamOverview.accuracyAnalysis.map(c => {
                        return {
                            difficulty: c.difficulty || "Unknown",
                            correct: c.correct || 0,
                            wrong: c.wrong || 0,
                            skipped: c.not_answered || 0,
                            accuracy: Math.round(c.correct_percentage || 0)
                        };
                    });
                }
                if (complexityStrength.length === 0) {
                    complexityStrength = mockDashboardData.complexityStrength;
                }

                let feeTerms = [];
                if (feeData?.getStudentFeeReport) {
                    feeData.getStudentFeeReport.forEach(fee => {
                        if (fee.terms && fee.terms.length > 0) {
                            fee.terms.forEach(term => {
                                feeTerms.push({
                                    term_no: term.term_no,
                                    term_amount: term.term_amount,
                                    term_date: term.term_date,
                                    is_paid: term.is_paid,
                                    paid_amount: term.paid_amount,
                                    paid_date: term.paid_date,
                                    fee_type_name: fee.fee_type_name
                                });
                            });
                        }
                    });
                }
                if (feeTerms.length === 0) {
                    feeTerms = mockDashboardData.feeTerms;
                }

                const weakAnalysis = analyticsData?.getAnalytics?.weak_analysis || mockDashboardData.weak_analysis;
                const complexityTimewise = timeAnalysisData?.getTimeAnalysisData?.complexity_timewise_data || mockDashboardData.complexity_timewise_data;
                const subjectTimewise = timeAnalysisData?.getTimeAnalysisData?.subject_timewise_data || mockDashboardData.subject_timewise_data;

                setLiveData({
                    studentProfile: {
                        name: student?.student_name || "Pramod Kumar",
                        mobile: studentMobile,
                        class: student?.class_name || "12th Standard",
                        batch: student?.stream_name || student?.exam_name || "NEET Elite-A",
                        branch: student?.branch_name || "Hyderabad Campus",
                        section: student?.section_name || "SEC-A1",
                        email: student?.email || mockDashboardData.studentProfile.email,
                        dateOfBirth: student?.date_of_birth || mockDashboardData.studentProfile.dateOfBirth,
                        gender: student?.gender || mockDashboardData.studentProfile.gender,
                        bloodGroup: student?.blood_group || mockDashboardData.studentProfile.bloodGroup,
                        avatarUrl: student?.avatar_url || mockDashboardData.studentProfile.avatarUrl,
                        
                        // Guardian Info
                        fatherName: student?.father_name || mockDashboardData.studentProfile.fatherName,
                        fatherContact: student?.father_contact || mockDashboardData.studentProfile.fatherContact,
                        motherName: student?.mother_name || mockDashboardData.studentProfile.motherName,
                        motherContact: student?.mother_contact || mockDashboardData.studentProfile.motherContact,
                        parentEmail: student?.parent_email || mockDashboardData.studentProfile.parentEmail,
                        residentialAddress: student?.residential_address || mockDashboardData.studentProfile.residentialAddress,
                        
                        // Enrollment Metadata
                        admissionDate: student?.admission_date || mockDashboardData.studentProfile.admissionDate,
                        hostelStatus: student?.hostel_status || mockDashboardData.studentProfile.hostelStatus,
                        transportMode: student?.transport_mode || mockDashboardData.studentProfile.transportMode,
                        transportDetails: student?.transport_details || mockDashboardData.studentProfile.transportDetails,
                        scholarshipDetails: student?.scholarship_details || mockDashboardData.studentProfile.scholarshipDetails,
                        
                        // Mentorship
                        mentorName: student?.mentor_name || mockDashboardData.studentProfile.mentorName,
                        mentorContact: student?.mentor_contact || mockDashboardData.studentProfile.mentorContact
                    },
                    kpis: {
                        avgAccuracy: Math.round(analyticsData?.getAnalytics?.strength || mockDashboardData.kpis.avgAccuracy),
                        currentRank: examOverviewData?.getStudentExamOverview?.overall?.avg_rank || mockDashboardData.kpis.currentRank,
                        totalSessions: overallDataRes?.getOverallData?.practice_data?.total_sessions || analyticsData?.getAnalytics?.total_practice || mockDashboardData.kpis.totalSessions,
                        avgSpeedSeconds: Math.round(analyticsData?.getAnalytics?.time_analysis?.speed || mockDashboardData.kpis.avgSpeedSeconds),
                        attendanceRate: Math.round(attendanceRate),
                        pendingFees: pendingFees
                    },
                    predictions: {
                        examType: student?.exam_name?.includes("JEE") ? "JEE" : "NEET",
                        predictedScore: predictedScore,
                        maxScore: student?.exam_name?.includes("JEE") ? 300 : 720,
                        estimatedAIR: Math.round(examOverviewData?.getStudentExamOverview?.overall?.avg_rank * 250) || mockDashboardData.predictions.estimatedAIR,
                        cutoffs: cutoffs
                    },
                    rankHistory,
                    exams: examsList,
                    subjectChapters,
                    classHeatmap,
                    timeAnalysis,
                    revisionPlan,
                    doubts: mockDashboardData.doubts,
                    batchComparison,
                    facultyPerformance: mockDashboardData.facultyPerformance,
                    dropoutRisk,
                    questionTheory,
                    complexityStrength,
                    feeTerms,
                    weak_analysis: weakAnalysis,
                    complexity_timewise_data: complexityTimewise,
                    subject_timewise_data: subjectTimewise,
                    learningHabits: mockDashboardData.learningHabits,
                    adminAlerts: mockDashboardData.adminAlerts
                });
                
                setRevisionPlanList(revisionPlan);

            } catch (err) {
                console.error("Error setting liveData:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [client, studentMobile, selectedClass, selectedExamType]);

    const handleAddDoubt = (e) => {
        e.preventDefault();
        if (!newDoubtTopic.trim() || !newDoubtQuestion.trim()) {
            alert("Please fill out all doubt fields.");
            return;
        }
        const newDoubt = {
            id: Date.now(),
            subject: newDoubtSubject,
            topic: newDoubtTopic,
            date: new Date().toLocaleDateString('en-GB').replace(/\//g, '-'),
            question: newDoubtQuestion,
            status: "PENDING",
            reply: "",
            teacher: ""
        };
        setDoubtsList([newDoubt, ...doubtsList]);
        setNewDoubtTopic('');
        setNewDoubtQuestion('');
        alert("Doubt raised successfully!");
    };

    const toggleRevisionStatus = (id) => {
        setRevisionPlanList(revisionPlanList.map(plan => 
            plan.id === id ? { ...plan, status: plan.status === 'COMPLETED' ? 'PLANNED' : 'COMPLETED' } : plan
        ));
    };

    if (loading) {
        return (
            <Container fluid className="analytics-dashboard-container py-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                <span className="ml-3 text-muted">Loading live student analytics...</span>
            </Container>
        );
    }

    return (
        <Container fluid className="analytics-dashboard-container py-4">
            {/* Header student profile metadata card */}
            <Card className="profile-metadata-card mb-4">
                <Card.Body className="d-flex align-items-center flex-wrap">
                    <Image 
                        src={require('../../../../images/businessman.png')} 
                        width="85" 
                        height="85" 
                        roundedCircle 
                        className="mr-3 border-accent" 
                        alt="student-avatar" 
                    />
                    <div className="metadata-text">
                        <div className="d-flex align-items-center">
                            <h2 className="m-0 student-name">{liveData.studentProfile.name}</h2>
                            <span className="badge-batch ml-3">{liveData.studentProfile.batch}</span>
                        </div>
                        <p className="m-0 student-sub-details mt-1 text-muted">
                            Class: {liveData.studentProfile.class} | 
                            Section: {liveData.studentProfile.section} | 
                            Branch: {liveData.studentProfile.branch} | 
                            Mobile: {liveData.studentProfile.mobile}
                        </p>
                    </div>
                </Card.Body>
            </Card>

            {/* Filter Bar Card */}
            <Card className="glass-card mb-4" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', borderColor: 'rgba(99, 102, 241, 0.15)', boxShadow: '0 4px 20px rgba(99, 102, 241, 0.05)' }}>
                <Card.Body className="d-flex align-items-center justify-content-between flex-wrap py-3">
                    <div>
                        <h5 className="m-0 font-weight-bold" style={{ fontSize: '15px', letterSpacing: '0.5px', color: '#0f172a' }}>
                            Academic Scope Filters
                        </h5>
                        <p className="m-0 font-11 text-muted mt-1">Affects Strengths, Speed &amp; Practice Metrics</p>
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <Form.Group className="mb-0 d-flex align-items-center">
                            <Form.Label className="m-0 mr-2 text-muted font-12" style={{ fontWeight: 600 }}>CLASS:</Form.Label>
                            <Form.Control 
                                as="select" 
                                value={selectedClass} 
                                onChange={(e) => setSelectedClass(e.target.value)}
                                className="form-dark-select py-1 px-3"
                                style={{ width: '160px', height: '38px', background: '#ffffff', border: '1px solid #cbd5e1', color: '#1e293b', borderRadius: '6px' }}
                            >
                                <option value="0">All Classes</option>
                                <option value="1">Class-XI</option>
                                <option value="2">Class-XII</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-0 d-flex align-items-center ml-3">
                            <Form.Label className="m-0 mr-2 text-muted font-12" style={{ fontWeight: 600 }}>EXAM MODE:</Form.Label>
                            <Form.Control 
                                as="select" 
                                value={selectedExamType} 
                                onChange={(e) => setSelectedExamType(e.target.value)}
                                className="form-dark-select py-1 px-3"
                                style={{ width: '160px', height: '38px', background: '#ffffff', border: '1px solid #cbd5e1', color: '#1e293b', borderRadius: '6px' }}
                            >
                                <option value="2">All Modes</option>
                                <option value="0">Practice</option>
                                <option value="1">Exam</option>
                            </Form.Control>
                        </Form.Group>
                    </div>
                </Card.Body>
            </Card>

            {/* KPI Cards Grid */}
            <Row className="mb-4">
                <Col xl={2} lg={4} md={6} sm={12} className="mb-3 flex-grow-1">
                    <Card className="kpi-card h-100">
                        <Card.Body>
                            <span className="kpi-label">Avg accuracy</span>
                            <h3 className="kpi-value color-green">{liveData.kpis.avgAccuracy}%</h3>
                            <div className="kpi-sub-text">Overall online exams</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={2} lg={4} md={6} sm={12} className="mb-3 flex-grow-1">
                    <Card className="kpi-card h-100">
                        <Card.Body>
                            <span className="kpi-label">Section rank</span>
                            <h3 className="kpi-value color-indigo">#{liveData.kpis.currentRank}</h3>
                            <div className="kpi-sub-text">Standing in {liveData.studentProfile.section}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={2} lg={4} md={6} sm={12} className="mb-3 flex-grow-1">
                    <Card className="kpi-card h-100">
                        <Card.Body>
                            <span className="kpi-label">Practice sessions</span>
                            <h3 className="kpi-value color-amber">{liveData.kpis.totalSessions}</h3>
                            <div className="kpi-sub-text">Sessions completed</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={2} lg={4} md={6} sm={12} className="mb-3 flex-grow-1">
                    <Card className="kpi-card h-100">
                        <Card.Body>
                            <span className="kpi-label">Avg speed</span>
                            <h3 className="kpi-value color-cyan">{liveData.kpis.avgSpeedSeconds}s</h3>
                            <div className="kpi-sub-text">Average per question</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={2} lg={4} md={6} sm={12} className="mb-3 flex-grow-1">
                    <Card className="kpi-card h-100">
                        <Card.Body>
                            <span className="kpi-label">Attendance Rate</span>
                            <h3 className="kpi-value color-green">{liveData.kpis.attendanceRate}%</h3>
                            <div className="kpi-sub-text">Current month stats</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Premium Navigation Tabs */}
            <Card className="dashboard-navigation-card mb-4 bg-transparent border-0">
                <Card.Body className="p-0">
                    <div className="nav-tabs-wrapper d-flex flex-wrap">
                        <Button 
                            className={`nav-tab-btn ${activeTab === 'overview' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('overview')}
                        >
                            Overview &amp; Predictions <span className="ml-1" style={{ color: '#f59e0b', textShadow: '0 0 5px #f59e0b' }}>●</span>
                        </Button>
                        <Button 
                            className={`nav-tab-btn ${activeTab === 'exams' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('exams')}
                        >
                            Exams &amp; Attempt Rate <span className="ml-1" style={{ color: '#10b981', textShadow: '0 0 5px #10b981' }}>●</span>
                        </Button>
                        <Button 
                            className={`nav-tab-btn ${activeTab === 'strengths' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('strengths')}
                        >
                            Strengths &amp; Heatmaps <span className="ml-1" style={{ color: '#f59e0b', textShadow: '0 0 5px #f59e0b' }}>●</span>
                        </Button>
                        <Button 
                            className={`nav-tab-btn ${activeTab === 'time' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('time')}
                        >
                            Speed &amp; Time <span className="ml-1" style={{ color: '#10b981', textShadow: '0 0 5px #10b981' }}>●</span>
                        </Button>
                        <Button 
                            className={`nav-tab-btn ${activeTab === 'planner' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('planner')}
                        >
                            Revision Planner <span className="ml-1" style={{ color: '#10b981', textShadow: '0 0 5px #10b981' }}>●</span>
                        </Button>
                        <Button 
                            className={`nav-tab-btn ${activeTab === 'insights' ? 'active' : ''}`} 
                            onClick={() => setActiveTab('insights')}
                        >
                            Batch &amp; Teacher Insights <span className="ml-1" style={{ color: '#f59e0b', textShadow: '0 0 5px #f59e0b' }}>●</span>
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Tab Contents */}
            <div className="tab-contents-container">

                {/* TAB 1: OVERVIEW */}
                {activeTab === 'overview' && (
                    <>
                        <Row>
                            <Col lg={4} md={12} className="mb-4">
                                <Card className="glass-card prediction-card h-100 relative">
                                    <Card.Body>
                                        <h4 className="card-title">Score Predictor ({liveData.predictions.examType}) <StatusBadge isLive={false} /></h4>
                                        <div className="predictor-main py-3 text-center">
                                            <div className="predicted-circle">
                                                <span className="predicted-label">Predicted Score</span>
                                                <span className="predicted-number color-green">{liveData.predictions.predictedScore}</span>
                                                <span className="predicted-max">/ {liveData.predictions.maxScore}</span>
                                            </div>
                                            <div className="air-box mt-3">
                                                <span className="air-badge">Est. All-India Rank (AIR): ~{liveData.predictions.estimatedAIR}</span>
                                            </div>
                                        </div>
                                        <h5 className="section-subtitle mt-3">Cutoff Benchmarking</h5>
                                        <div className="cutoff-list">
                                            {liveData.predictions.cutoffs.map((item, idx) => (
                                                <div key={idx} className="cutoff-item d-flex justify-content-between align-items-center py-2 border-bottom-dim">
                                                    <span className="cutoff-category">{item.category}</span>
                                                    <span className="cutoff-marks">Cutoff: {item.score}</span>
                                                    <span className="badge-success-pill">{item.status}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
     
                            <Col lg={8} md={12} className="mb-4">
                                <Card className="glass-card rank-history-card h-100">
                                    <Card.Body>
                                        <h4 className="card-title">Rank Movement Over Time <StatusBadge isLive={true} /></h4>
                                        <p className="card-subtitle text-muted">Weekly improvements across section, branch, and state bounds</p>
                                        <div className="chart-wrapper mt-4" style={{ width: '100%', height: 280 }}>
                                            <ResponsiveContainer>
                                                <LineChart data={liveData.rankHistory} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                                    <XAxis dataKey="week" stroke="#64748b" tickLine={false} />
                                                    <YAxis reversed stroke="#64748b" tickLine={false} />
                                                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: 'rgba(0,0,0,0.08)', color: '#1e293b', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                                                    <Legend />
                                                    <Line type="monotone" dataKey="section" name="Section Rank" stroke="#4f46e5" strokeWidth={2.5} activeDot={{ r: 8 }} />
                                                    <Line type="monotone" dataKey="branch" name="Branch Rank" stroke="#0ca678" strokeWidth={2} />
                                                    <Line type="monotone" dataKey="city" name="City Rank" stroke="#0891b2" strokeWidth={1.8} />
                                                    <Line type="monotone" dataKey="state" name="State Rank" stroke="#e11d48" strokeWidth={1.5} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <div className="my-4 pt-2">
                            <h4 className="font-weight-bold" style={{ fontSize: '18px', color: '#0f172a', letterSpacing: '0.5px' }}>
                                Student Profile &amp; Enrollment Records
                            </h4>
                            <p className="font-12 text-muted m-0 mb-3">Comprehensive administrative and guardian registry logs</p>
                        </div>

                        <Row>
                            <Col lg={6} md={12} className="mb-4">
                                <Card className="glass-card mb-4">
                                    <Card.Body>
                                        <h5 className="font-weight-bold font-14 mb-3" style={{ color: '#1e293b' }}>Personal Demographics</h5>
                                        <Table bordered hover responsive className="font-13 m-0">
                                            <tbody>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim" style={{ width: '35%' }}>Full Name</td>
                                                    <td>{liveData.studentProfile.name}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Email Address</td>
                                                    <td>{liveData.studentProfile.email}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Mobile Number</td>
                                                    <td>{liveData.studentProfile.mobile}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Date of Birth</td>
                                                    <td>{liveData.studentProfile.dateOfBirth ? moment(liveData.studentProfile.dateOfBirth).format("DD MMMM YYYY") : "N/A"}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Gender / Blood Group</td>
                                                    <td>{liveData.studentProfile.gender} / <span className="badge-danger-pill">{liveData.studentProfile.bloodGroup}</span></td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>

                                <Card className="glass-card">
                                    <Card.Body>
                                        <h5 className="font-weight-bold font-14 mb-3" style={{ color: '#1e293b' }}>Guardian &amp; Emergency Contacts</h5>
                                        <Table bordered hover responsive className="font-13 m-0">
                                            <tbody>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim" style={{ width: '35%' }}>Father's Name</td>
                                                    <td>{liveData.studentProfile.fatherName} (Mob: {liveData.studentProfile.fatherContact})</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Mother's Name</td>
                                                    <td>{liveData.studentProfile.motherName} (Mob: {liveData.studentProfile.motherContact})</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Parent Email</td>
                                                    <td>{liveData.studentProfile.parentEmail}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Residential Address</td>
                                                    <td style={{ whiteSpace: 'pre-wrap' }}>{liveData.studentProfile.residentialAddress}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={6} md={12} className="mb-4">
                                <Card className="glass-card mb-4">
                                    <Card.Body>
                                        <h5 className="font-weight-bold font-14 mb-3" style={{ color: '#1e293b' }}>Institutional Enrollment Records</h5>
                                        <Table bordered hover responsive className="font-13 m-0">
                                            <tbody>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim" style={{ width: '35%' }}>Admission Date</td>
                                                    <td>{liveData.studentProfile.admissionDate ? moment(liveData.studentProfile.admissionDate).format("DD-MM-YYYY") : "N/A"}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Hostel Boarding</td>
                                                    <td>
                                                        <span className={liveData.studentProfile.hostelStatus === 'HOSTELER' ? 'badge-danger-pill' : 'badge-success-pill'}>
                                                            {liveData.studentProfile.hostelStatus === 'HOSTELER' ? 'Hostel Boarder' : 'Day Scholar'}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Transport Details</td>
                                                    <td>{liveData.studentProfile.transportMode === 'COLLEGE_BUS' ? `College Bus (${liveData.studentProfile.transportDetails})` : 'Private Transport'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold bg-light-dim">Scholarship Scheme</td>
                                                    <td><span className="font-weight-bold text-success">{liveData.studentProfile.scholarshipDetails}</span></td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>

                                <Card className="glass-card">
                                    <Card.Body>
                                        <h5 className="font-weight-bold font-14 mb-3" style={{ color: '#1e293b' }}>Assigned Academic Mentor</h5>
                                        <div className="d-flex align-items-center p-3 border-accent-left bg-light-dim rounded">
                                            <Image src={require('../../../../images/businessman.png')} width="50" height="50" roundedCircle className="mr-3" />
                                            <div>
                                                <h5 className="m-0 font-weight-bold font-14">{liveData.studentProfile.mentorName}</h5>
                                                <p className="m-0 font-11 text-muted mt-1">Student Success Mentor &amp; Counselor</p>
                                                <p className="m-0 font-11 color-indigo font-weight-bold mt-1">Contact: {liveData.studentProfile.mentorContact}</p>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
 
                {/* TAB 2: EXAMS & ATTEMPT RATE */}
                {activeTab === 'exams' && (
                    <Card className="glass-card mb-4">
                        <Card.Body>
                            <h4 className="card-title">Exams History &amp; Attempt Rates <StatusBadge isLive={true} /></h4>
                            <div className="table-responsive mt-3">
                                <Table className="custom-dashboard-table">
                                    <thead>
                                        <tr>
                                            <th>Exam Name</th>
                                            <th>Date</th>
                                            <th>Score</th>
                                            <th>Accuracy</th>
                                            <th>Attempt Ratio (% Correct / Wrong / Skipped)</th>
                                            <th>Ranks (Sec/Branch/State)</th>
                                            <th>Percentiles <StatusBadge isLive={false} label="Demo" /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {liveData.exams.map((exam, idx) => {
                                            const totalQ = exam.correct + exam.wrong + exam.skipped || 1;
                                            const correctPct = (exam.correct / totalQ) * 100;
                                            const wrongPct = (exam.wrong / totalQ) * 100;
                                            const skippedPct = (exam.skipped / totalQ) * 100;

                                            return (
                                                <tr key={exam.id || idx}>
                                                    <td className="font-weight-bold">{exam.name}</td>
                                                    <td>{exam.date}</td>
                                                    <td>
                                                        <span className="font-weight-bold text-dark">{exam.score}</span>
                                                        <span className="text-muted"> / {exam.maxScore}</span>
                                                    </td>
                                                    <td className="color-green font-weight-bold">{exam.accuracy}%</td>
                                                    <td style={{ width: '220px' }}>
                                                        <div className="attempt-stacked-bar">
                                                            <div className="correct-bar" style={{ width: `${correctPct}%` }} title={`Correct: ${exam.correct}`} />
                                                            <div className="wrong-bar" style={{ width: `${wrongPct}%` }} title={`Wrong: ${exam.wrong}`} />
                                                            <div className="skipped-bar" style={{ width: `${skippedPct}%` }} title={`Skipped: ${exam.skipped}`} />
                                                        </div>
                                                        <div className="d-flex justify-content-between font-10 mt-1 text-muted">
                                                            <span>C: {exam.correct}</span>
                                                            <span>W: {exam.wrong}</span>
                                                            <span>S: {exam.skipped}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge-sec">S: #{exam.sectionRank}</span>
                                                        <span className="badge-br ml-1">B: #{exam.branchRank}</span>
                                                        <span className="badge-st ml-1">St: #{exam.stateRank}</span>
                                                    </td>
                                                    <td>
                                                        <div className="font-weight-bold">B: {exam.batchPercentile}%</div>
                                                        <div className="text-muted font-11">All: {exam.overallPercentile}%</div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {/* TAB 3: STRENGTHS & WEAKNESSES */}
                {activeTab === 'strengths' && (
                    <>
                        <Row>
                            <Col lg={7} md={12} className="mb-4">
                                <Card className="glass-card h-100">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                                            <div className="d-flex align-items-center">
                                                <h4 className="card-title m-0">Chapter Strengths</h4>
                                                <StatusBadge isLive={true} />
                                            </div>
                                            <div className="subject-pills-bar mt-2 mt-md-0">
                                                {Object.keys(liveData.subjectChapters).map((sub) => (
                                                    <Button
                                                        key={sub}
                                                        className={`subject-pill-btn ${selectedSubject === sub ? 'active' : ''}`}
                                                        onClick={() => setSelectedSubject(sub)}
                                                    >
                                                        {sub}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="chapters-grid mt-4">
                                            {liveData.subjectChapters[selectedSubject]?.map((chapter, idx) => {
                                                const statusClass = 
                                                    chapter.status === 'Strong' ? 'badge-strong' :
                                                    chapter.status === 'Average' ? 'badge-average' : 'badge-weak';
                                                return (
                                                    <div key={idx} className="chapter-performance-row d-flex justify-content-between align-items-center py-2 border-bottom-dim">
                                                        <span className="chapter-name">{chapter.name}</span>
                                                        <div className="d-flex align-items-center">
                                                            <span className="accuracy-label mr-3">{chapter.accuracy}% Accuracy</span>
                                                            <span className={`status-pill ${statusClass}`}>{chapter.status}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={5} md={12} className="mb-4">
                                <Card className="glass-card h-100 relative">
                                    <Card.Body>
                                        <div className="d-flex align-items-center mb-2">
                                            <h4 className="card-title m-0">Class-Wide Weak Area Heatmap</h4>
                                            <StatusBadge isLive={false} />
                                        </div>
                                        <p className="card-subtitle text-muted">Chapters where the majority of students in {liveData.studentProfile.section} struggle</p>
                                        <div className="heatmap-list mt-4">
                                            {liveData.classHeatmap.map((item, idx) => (
                                                <div key={idx} className="heatmap-item p-3 mb-3 border-accent-left">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="heatmap-chapter font-weight-bold">{item.chapter}</span>
                                                        <span className="color-red font-weight-bold">{item.underperformingPercentage}% Students</span>
                                                    </div>
                                                    <div className="progress-bar-flat-bg mt-2">
                                                        <div className="progress-bar-flat-fill bg-danger" style={{ width: `${item.underperformingPercentage}%` }} />
                                                    </div>
                                                    <div className="font-10 text-muted mt-1">Underperforming (accuracy below 50%)</div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <Col xs={12}>
                                <Card className="glass-card">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center">
                                                <h4 className="card-title m-0">Answering Performance by Complexity Level</h4>
                                                <StatusBadge isLive={true} />
                                            </div>
                                        </div>
                                        <p className="card-subtitle text-muted mb-4">Accuracy rate and question metrics segmented by difficulty tiers</p>
                                        <div className="table-responsive">
                                            <Table className="custom-dashboard-table">
                                                <thead>
                                                    <tr>
                                                        <th>Complexity Level</th>
                                                        <th>Correct Answers</th>
                                                        <th>Wrong Answers</th>
                                                        <th>Skipped / Unanswered</th>
                                                        <th>Accuracy Rate</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {liveData.complexityStrength.map((c, idx) => (
                                                        <tr key={idx} className="complexity-grid-row">
                                                            <td className="font-weight-bold">{c.difficulty}</td>
                                                            <td>{c.correct}</td>
                                                            <td>{c.wrong}</td>
                                                            <td>{c.skipped}</td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <span className="font-weight-bold mr-3" style={{ minWidth: '45px' }}>{c.accuracy}%</span>
                                                                    <div className="progress-bar-flat-bg flex-grow-1" style={{ maxWidth: '180px' }}>
                                                                        <div 
                                                                            className={`progress-bar-flat-fill ${c.accuracy >= 70 ? 'bg-success' : c.accuracy >= 45 ? 'bg-warning' : 'bg-danger'}`} 
                                                                            style={{ width: `${c.accuracy}%` }} 
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <Col xs={12}>
                                <Card className="glass-card">
                                    <Card.Body>
                                        <div className="d-flex align-items-center mb-3">
                                            <h4 className="card-title m-0">Question Type Weakness Profiler</h4>
                                            <StatusBadge isLive={true} />
                                        </div>
                                        <p className="card-subtitle text-muted mb-4">Accuracy rates segmented by question structure and format</p>
                                        <div className="mt-3">
                                            {liveData.weak_analysis && liveData.weak_analysis.map((item, idx) => (
                                                <div key={idx} className="mb-3">
                                                    <div className="d-flex justify-content-between font-12 font-weight-bold">
                                                        <span>{item.question_type} ({item.count} questions)</span>
                                                        <span className={item.accuracy < 60 ? "color-red" : "color-green"}>{item.accuracy}% Accuracy</span>
                                                    </div>
                                                    <div className="progress-bar-flat-bg mt-2">
                                                        <div 
                                                            className={`progress-bar-flat-fill ${item.accuracy < 60 ? 'bg-danger' : 'bg-success'}`} 
                                                            style={{ width: `${item.accuracy}%` }} 
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}

                {/* TAB 4: SPEED & TIME */}
                {activeTab === 'time' && (
                    <>
                        <Row>
                            <Col lg={5} md={12} className="mb-4">
                                <Card className="glass-card h-100">
                                    <Card.Body>
                                        <div className="d-flex align-items-center mb-2">
                                            <h4 className="card-title m-0">Answering Time Tradeoffs</h4>
                                            <StatusBadge isLive={true} />
                                        </div>
                                        <p className="card-subtitle text-muted">Accuracy based on spent question durations</p>
                                        <div className="time-tradeoffs-list mt-4">
                                            <div className="tradeoff-card p-3 mb-3 border-green-left">
                                                <div className="d-flex justify-content-between">
                                                    <span className="tradeoff-title">In-Time Answering</span>
                                                    <span className="tradeoff-ratio color-green">{liveData.timeAnalysis.inTime.percentage}% of test</span>
                                                </div>
                                                <div className="tradeoff-accuracy mt-1 font-weight-bold">Accuracy: {liveData.timeAnalysis.inTime.accuracy}%</div>
                                                <div className="text-muted font-11 mt-1">Optimal answering speed.</div>
                                            </div>
                                            <div className="tradeoff-card p-3 mb-3 border-red-left">
                                                <div className="d-flex justify-content-between">
                                                    <span className="tradeoff-title">Less-Time Answering (Rushed)</span>
                                                    <span className="tradeoff-ratio color-red">{liveData.timeAnalysis.lessTime.percentage}% of test</span>
                                                </div>
                                                <div className="tradeoff-accuracy mt-1 font-weight-bold">Accuracy: {liveData.timeAnalysis.lessTime.accuracy}%</div>
                                                <div className="text-muted font-11 mt-1">Rushed attempts yielding high mistake rates.</div>
                                            </div>
                                            <div className="tradeoff-card p-3 mb-3 border-amber-left">
                                                <div className="d-flex justify-content-between">
                                                    <span className="tradeoff-title">Overtime Answering</span>
                                                    <span className="tradeoff-ratio color-amber">{liveData.timeAnalysis.overTime.percentage}% of test</span>
                                                </div>
                                                <div className="tradeoff-accuracy mt-1 font-weight-bold">Accuracy: {liveData.timeAnalysis.overTime.accuracy}%</div>
                                                <div className="text-muted font-11 mt-1">High accuracy but compromises overall test time.</div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={7} md={12} className="mb-4">
                                <Card className="glass-card h-100">
                                    <Card.Body>
                                        <div className="d-flex align-items-center mb-2">
                                            <h4 className="card-title m-0">Answering Speed vs. Accuracy</h4>
                                            <StatusBadge isLive={true} />
                                        </div>
                                        <div className="chart-wrapper mt-4" style={{ width: '100%', height: 260 }}>
                                            <ResponsiveContainer>
                                                <ScatterChart margin={{ top: 20, right: 20, bottom: 0, left: -10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                                    <XAxis dataKey="timeBucket" name="Time Spent" stroke="#64748b" />
                                                    <YAxis dataKey="accuracy" name="Accuracy" unit="%" stroke="#64748b" />
                                                    <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ backgroundColor: '#ffffff', borderColor: 'rgba(0,0,0,0.08)', color: '#1e293b', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                                                    <Scatter name="Answering Stats" data={liveData.timeAnalysis.speedTrend} fill="#4f46e5" />
                                                </ScatterChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <Col xs={12}>
                                <Card className="glass-card">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center">
                                                <h4 className="card-title m-0">Application vs. Theory Answering Patterns</h4>
                                                <StatusBadge isLive={true} />
                                            </div>
                                        </div>
                                        <p className="card-subtitle text-muted mb-4">Differentiating analysis between conceptual-theory questions and application-numerical problems</p>
                                        
                                        <Row>
                                            <Col md={6} className="mb-3">
                                                <div className="tradeoff-card p-4 border-accent-left">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="font-weight-bold" style={{ fontSize: '16px' }}>Theory-based Questions</span>
                                                        <span className="badge-batch">{liveData.questionTheory.theoryQuestions} Questions</span>
                                                    </div>
                                                    <div className="d-flex align-items-baseline mb-2">
                                                        <h3 className="m-0 color-indigo font-weight-bold">{liveData.questionTheory.theoryAccuracy}%</h3>
                                                        <span className="text-muted ml-2">Accuracy</span>
                                                    </div>
                                                    <div className="progress-bar-flat-bg" style={{ height: '8px' }}>
                                                        <div className="progress-bar-flat-fill" style={{ width: `${liveData.questionTheory.theoryAccuracy}%`, background: '#818cf8' }} />
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="tradeoff-card p-4 border-green-left">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="font-weight-bold" style={{ fontSize: '16px' }}>Application &amp; Numerical Questions</span>
                                                        <span className="badge-batch">{liveData.questionTheory.applicationQuestions} Questions</span>
                                                    </div>
                                                    <div className="d-flex align-items-baseline mb-2">
                                                        <h3 className="m-0 color-green font-weight-bold">{liveData.questionTheory.applicationAccuracy}%</h3>
                                                        <span className="text-muted ml-2">Accuracy</span>
                                                    </div>
                                                    <div className="progress-bar-flat-bg" style={{ height: '8px' }}>
                                                        <div className="progress-bar-flat-fill bg-success" style={{ width: `${liveData.questionTheory.applicationAccuracy}%` }} />
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <Col xs={12}>
                                <Card className="glass-card">
                                    <Card.Body>
                                        <div className="d-flex align-items-center mb-3">
                                            <h4 className="card-title m-0">Answering Speeds by Difficulty Tiers</h4>
                                            <StatusBadge isLive={true} />
                                        </div>
                                        <p className="card-subtitle text-muted mb-4">Time management distribution segmented by question complexity</p>
                                        <div style={{ width: '100%', height: 280 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={liveData.complexity_timewise_data} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                                    <XAxis dataKey="complexity" stroke="#64748b" />
                                                    <YAxis unit="%" stroke="#64748b" />
                                                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: 'rgba(0,0,0,0.08)', color: '#1e293b', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                                                    <Legend />
                                                    <Bar dataKey="intime" name="In-Time %" stackId="a" fill="#0ca678" />
                                                    <Bar dataKey="lesstime" name="Rushed %" stackId="a" fill="#e11d48" />
                                                    <Bar dataKey="overtime" name="Overtime %" stackId="a" fill="#d97706" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <div className="my-4 pt-2">
                            <h4 className="font-weight-bold" style={{ fontSize: '18px', color: '#0f172a', letterSpacing: '0.5px' }}>
                                Cognitive Learning Habits
                            </h4>
                            <p className="font-12 text-muted m-0 mb-3">Analysis of answering speeds, concentration windows, and behavioral patterns</p>
                        </div>

                        <Row className="mb-4">
                            <Col lg={4} md={12} className="mb-4">
                                <Card className="glass-card h-100">
                                    <Card.Body>
                                        <h5 className="font-weight-bold font-14 mb-3" style={{ color: '#1e293b' }}>Habits Profile</h5>
                                        
                                        <div className="habit-metric-box mb-3 p-3 border-accent-left">
                                            <div className="d-flex justify-content-between font-13 font-weight-bold">
                                                <span>Rushed Attempt Rate (Guessing Risk)</span>
                                                <span className="color-amber">{liveData.learningHabits.rushedAttemptRate}%</span>
                                            </div>
                                            <div className="progress-bar-flat-bg mt-2">
                                                <div className="progress-bar-flat-fill bg-warning" style={{ width: `${liveData.learningHabits.rushedAttemptRate}%` }} />
                                            </div>
                                            <div className="font-10 text-muted mt-1">Answering in under 20% of standard allotted time.</div>
                                        </div>

                                        <div className="habit-metric-box mb-3 p-3 border-red-left">
                                            <div className="d-flex justify-content-between font-13 font-weight-bold">
                                                <span>Overtime Attempt Rate (Time Trap)</span>
                                                <span className="color-red">{liveData.learningHabits.overtimeAttemptRate}%</span>
                                            </div>
                                            <div className="progress-bar-flat-bg mt-2">
                                                <div className="progress-bar-flat-fill bg-danger" style={{ width: `${liveData.learningHabits.overtimeAttemptRate}%` }} />
                                            </div>
                                            <div className="font-10 text-muted mt-1">Spending excessive time per question.</div>
                                        </div>

                                        <div className="habit-metric-box mb-3 p-3 border-green-left">
                                            <div className="d-flex justify-content-between font-13 font-weight-bold">
                                                <span>Optimal Speed Accuracy</span>
                                                <span className="color-green">{liveData.learningHabits.correctInTimeRate}%</span>
                                            </div>
                                            <div className="progress-bar-flat-bg mt-2">
                                                <div className="progress-bar-flat-fill bg-success" style={{ width: `${liveData.learningHabits.correctInTimeRate}%` }} />
                                            </div>
                                            <div className="font-10 text-muted mt-1">Accuracy when answering within standard time limits.</div>
                                        </div>

                                        <div className="habit-metric-box p-3 border-accent-left">
                                            <div className="font-13 font-weight-bold">Peak Concentration Hours</div>
                                            <div className="font-18 color-indigo font-weight-bold mt-1">{liveData.learningHabits.peakLearningHours}</div>
                                            <div className="font-10 text-muted mt-1">Hours of highest consistent practice volume.</div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={8} md={12} className="mb-4">
                                <Card className="glass-card h-100">
                                    <Card.Body>
                                        <h5 className="font-weight-bold font-14 mb-3" style={{ color: '#1e293b' }}>Answering Speed &amp; Accuracy Quadrants</h5>
                                        <p className="card-subtitle text-muted mb-4">Correlation of speed categories with success rates</p>
                                        <div className="chart-wrapper mt-4" style={{ width: '100%', height: 280 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={liveData.learningHabits.answeringQuadrants} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                                    <XAxis dataKey="category" stroke="#64748b" />
                                                    <YAxis stroke="#64748b" unit="%" />
                                                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: 'rgba(0,0,0,0.08)', color: '#1e293b', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                                                    <Bar dataKey="value" fill="#4f46e5">
                                                        {liveData.learningHabits.answeringQuadrants.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}

                {/* TAB 5: REVISION PLANNER */}
                {activeTab === 'planner' && (
                    <Card className="glass-card mb-4 relative">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                                <div className="d-flex align-items-center">
                                    <h4 className="card-title m-0">Auto-Generated Study &amp; Revision Planner</h4>
                                    <StatusBadge isLive={true} />
                                </div>
                                <StatusBadge isLive={false} label="Persistence: Offline Demo" />
                            </div>
                            <p className="card-subtitle text-muted">Weekly schedule focusing on weak chapters (<span className="color-red">&lt;60% accuracy</span>)</p>
                            <div className="planner-tasks-list mt-4">
                                {revisionPlanList.map((plan) => (
                                    <div 
                                        key={plan.id} 
                                        className={`planner-task-item p-3 mb-3 d-flex justify-content-between align-items-center flex-wrap ${plan.status === 'COMPLETED' ? 'task-completed-dim' : ''}`}
                                    >
                                        <div>
                                            <span className="task-date color-indigo font-weight-bold mr-3">{plan.date}</span>
                                            <span className="task-subject badge-subject mr-2">{plan.subject}</span>
                                            <span className="task-chapter font-weight-bold">{plan.chapter}</span>
                                            <div className="task-topic mt-1 text-muted font-11">Topic: {plan.topic} | Initial Accuracy: <span className="color-red">{plan.accuracy}%</span></div>
                                        </div>
                                        <div className="mt-2 mt-md-0">
                                            {/* <Button 
                                                className={`action-btn-flat ${plan.status === 'COMPLETED' ? 'btn-success' : 'btn-outline-primary'}`} 
                                                onClick={() => toggleRevisionStatus(plan.id)}
                                            >
                                                {plan.status === 'COMPLETED' ? 'Completed' : 'Mark Completed'}
                                            </Button> */}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                )}


                {/* TAB 7: BATCH & TEACHER INSIGHTS */}
                {activeTab === 'insights' && (
                    <><Row>
                        <Col lg={7} md={12} className="mb-4">
                            <Card className="glass-card h-100 relative">
                                <Card.Body>
                                    <div className="d-flex align-items-center mb-3">
                                        <h4 className="card-title m-0">Batch-wise Average Score Comparison</h4>
                                        <StatusBadge isLive={true} />
                                    </div>
                                    <div className="chart-wrapper mt-4" style={{ width: '100%', height: 350 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={liveData.batchComparison} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                                <XAxis dataKey="section" stroke="#64748b" />
                                                <YAxis stroke="#64748b" />
                                                <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderColor: 'rgba(0,0,0,0.08)', color: '#1e293b', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                                                <Legend />
                                                <Bar dataKey="Physics" fill="#4f46e5" />
                                                <Bar dataKey="Chemistry" fill="#0ca678" />
                                                <Bar dataKey="Biology" fill="#d97706" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={5} md={12} className="mb-4">
                            <Card className="glass-card mb-4 relative">
                                <Card.Body>
                                    <div className="d-flex align-items-center mb-3">
                                        <h4 className="card-title m-0">Faculty-wise Class Outcomes</h4>
                                        <StatusBadge isLive={false} />
                                    </div>
                                    <div className="teacher-grid mt-3">
                                        {liveData.facultyPerformance.map((teacher, idx) => (
                                            <div key={idx} className="teacher-item p-3 mb-3 border-accent-left">
                                                <div className="d-flex justify-content-between">
                                                    <span className="teacher-name font-weight-bold">{teacher.name}</span>
                                                    <span className="color-green font-weight-bold">{teacher.avgAccuracy}% Accuracy</span>
                                                </div>
                                                <div className="text-muted font-11 mt-1">Subject: {teacher.subject} | Classes: {teacher.sections}</div>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>



                            <Card className="glass-card relative">
                                <Card.Body>
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="d-flex align-items-center">
                                            <h4 className="card-title m-0">Term-Wise Fees Statement</h4>
                                            <StatusBadge isLive={true} />
                                        </div>
                                    </div>
                                    <p className="card-subtitle text-muted mb-3 font-11">Status of scheduled fee installments for the current academic year</p>
                                    <div className="fee-terms-list mt-3">
                                        {liveData.feeTerms.map((term, idx) => (
                                            <div key={idx} className="term-fee-item p-3 mb-2 d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="font-weight-bold font-13">Term {term.term_no} {term.fee_type_name ? `- ${term.fee_type_name}` : ''}</div>
                                                    <div className="text-muted font-11 mt-1">
                                                        Due Date: {term.term_date ? (parseResilientDate(term.term_date)?.format("DD-MM-YYYY") || "N/A") : "N/A"}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-weight-bold font-13">₹{term.term_amount.toLocaleString()}</div>
                                                    <div className="mt-1">
                                                        {term.is_paid ? (
                                                            <span className="badge-success-pill">Paid ₹{term.paid_amount.toLocaleString()}</span>
                                                        ) : (
                                                            <span className="badge-danger-pill">Unpaid</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row><div className="my-4 pt-2">
                            <h4 className="font-weight-bold" style={{ fontSize: '18px', color: '#0f172a', letterSpacing: '0.5px' }}>
                                Calculated Risk &amp; Operational Alerts
                            </h4>
                            <p className="font-12 text-muted m-0 mb-3">System-wide monitoring flags and watchlist alerts</p>
                        </div><Row className="mb-4">
                            <Col lg={4} md={12} className="mb-4">
                                <Card className="glass-card text-center py-4 h-100">
                                    <Card.Body className="d-flex flex-column justify-content-center align-items-center">
                                        <h5 className="font-weight-bold font-14 mb-4" style={{ color: '#1e293b' }}>Student Dropout Risk Index</h5>
                                        <div className="risk-gauge-circle my-3">
                                            <span className="risk-percentage text-danger">{liveData.adminAlerts.dropoutRiskPercentage}%</span>
                                            <span className="risk-label text-muted">Risk Ratio</span>
                                        </div>
                                        <div className="risk-badge-box mt-3">
                                            <span className="badge-danger-pill" style={{ fontSize: '12px', padding: '6px 16px' }}>
                                                {liveData.adminAlerts.dropoutRiskLevel} RISK
                                            </span>
                                        </div>
                                        <div className="text-left font-11 text-muted mt-3 w-100 p-3 bg-light-dim rounded" style={{ borderLeft: '3px solid #ef4444' }}>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span>Score Trend Risk:</span>
                                                <span className="font-weight-bold text-dark">{liveData.dropoutRisk[0]?.scoreRisk || "Low"}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span>Attendance Risk:</span>
                                                <span className="font-weight-bold text-dark">{liveData.dropoutRisk[0]?.attendanceRisk || "Low"}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>Financial Risk:</span>
                                                <span className="font-weight-bold text-dark">{liveData.dropoutRisk[0]?.feeRisk || "Cleared"}</span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={8} md={12} className="mb-4">
                                <Card className="glass-card h-100">
                                    <Card.Body>
                                        <h5 className="font-weight-bold font-14 mb-3" style={{ color: '#1e293b' }}>Administrative Watchlist Warnings</h5>
                                        <p className="card-subtitle text-muted mb-4">Important operational notifications needing administrative attention</p>

                                        <div className="alerts-timeline">
                                            {liveData.adminAlerts.alerts.map((alert) => {
                                                const borderClass = alert.type === 'ALERT' ? 'border-red-left' :
                                                    alert.type === 'WARNING' ? 'border-amber-left' : 'border-green-left';
                                                const badgeClass = alert.type === 'ALERT' ? 'badge-danger-pill' :
                                                    alert.type === 'WARNING' ? 'badge-warning-pill' : 'badge-success-pill';
                                                return (
                                                    <div key={alert.id} className={`alert-timeline-item p-3 mb-3 d-flex justify-content-between align-items-center flex-wrap ${borderClass}`}>
                                                        <div>
                                                            <span className="text-muted font-11 mr-3">{alert.date}</span>
                                                            <span className="font-weight-bold font-13">{alert.message}</span>
                                                        </div>
                                                        <span className={badgeClass}>{alert.type}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row></>
                )}
            </div>
        </Container>
    );
};

export default StudentProfileAnalyticsSection;
